'use client';

import React, { memo } from 'react';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';
import AgentHandoffAnimation from './AgentHandoffAnimation';
import { ReviewerCallout } from './ReviewerCallout';
import { ClarificationAckNotice } from './ClarificationAckNotice';
import { DegradationNotice } from './DegradationNotice';
import { MessageFeedbackControls } from './MessageFeedbackControls';
import { MemoryPill } from './MemoryPill';
import { ArtifactReferenceCard } from './ArtifactReferenceCard';
import { RuntimeToolResultPanel } from '@/components/agent-tools/RuntimeToolResultPanel';
import type { InteractionFeedback } from '@/types';
import type { ReviewerFeedback } from '@/types/reviewer';

interface ArticleMessageProps {
  message: any;
  isUser: boolean;
  fullWidth: boolean;
  compact: boolean;
  currentUser: any;
  activeClient: any;
  error?: boolean;
  onFeedback?: (messageId: string, feedback: InteractionFeedback) => Promise<void>;
  onArtifactClick?: (artifactId: string) => void;
  reviewerFeedback?: ReviewerFeedback;
  onRequestChanges?: (feedback: ReviewerFeedback) => void;
  onViewDetails?: (feedback: ReviewerFeedback) => void;
}

const ArticleMessage = memo(function ArticleMessage({
  message,
  isUser,
  fullWidth,
  compact,
  currentUser,
  activeClient,
  error,
  onFeedback,
  onArtifactClick,
  reviewerFeedback,
  onRequestChanges,
  onViewDetails,
}: ArticleMessageProps) {
  const messageId = message?.id;
  const meta = message?.meta;
  const memories = Array.isArray(meta?.memories) ? meta.memories : [];
  const policyIssues = Array.isArray(meta?.policyIssues) ? meta.policyIssues : [];
  const degradations = Array.isArray(meta?.provenance?.degradations) ? meta.provenance.degradations : [];
  const clarificationAck = meta?.clarificationAck;
  const runtimeTool = meta?.runtimeTool;
  const artifactReference = message?.isArtifactReference ? message?.artifactReference : null;
  const agentMetadata = message?.agentMetadata;
  const agentHandoffData = message?.agentHandoffData;
  const isEdited = message?.isEdited;
  const editedAt = message?.editedAt;

  const formatTimestamp = (t: any): string => {
    if (!t) return 'Just now';
    try {
      let d: Date;
      if (typeof t === 'object' && t?.toDate) d = t.toDate();
      else if (t instanceof Date) d = t;
      else if (typeof t === 'number' || typeof t === 'string') d = new Date(t);
      else return 'Just now';
      if (isNaN(d.getTime())) return 'Just now';
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return 'Just now';
    }
  };

  const formatEditedAt = (t: any): string => {
    if (!t) return '';
    try {
      let date: Date;
      if (t instanceof Date) date = t;
      else if (typeof t === 'object' && t.toDate) date = t.toDate();
      else date = new Date(t);
      return isNaN(date.getTime()) ? '' : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  return (
    <article
      className={cn(
        'group py-5 px-6 transition-all duration-200',
        isUser ? 'article-glass-user' : 'article-glass-ai',
        error && 'ring-2 ring-inset ring-destructive/50 bg-destructive/5 animate-pulse',
      )}
    >
      <div className={cn(fullWidth ? 'max-w-none' : 'max-w-3xl mx-auto')}>
        {/* Header: avatar + name + timestamp */}
        {!compact && (
          <div className="flex items-center gap-3 mb-3">
            <div
              className={cn(
                'w-8 h-8 rounded-lg flex items-center justify-center text-xs font-semibold shadow-sm backdrop-blur-sm',
                isUser
                  ? 'bg-gradient-to-br from-blue-500 to-purple-600 text-white'
                  : 'bg-gradient-to-br from-purple-500 to-pink-600 text-white',
              )}
            >
              {isUser ? 'U' : 'AI'}
            </div>
            <div className="flex-1">
              <div className="font-medium text-sm">
                {isUser
                  ? currentUser?.displayName || currentUser?.email || 'You'
                  : 'MiComms AI'}
              </div>
              <div className="text-xs text-muted-foreground">
                {formatTimestamp(message?.timestamp)}
              </div>
            </div>
          </div>
        )}

        {/* Agent handoff animation */}
        {!isUser && agentHandoffData && (message?.isLoading || agentHandoffData.phase === 'complete') && (
          <div className="mb-3">
            <AgentHandoffAnimation
              selectedAgent={agentHandoffData.selectedAgent}
              phase={agentHandoffData.phase}
              processingSteps={agentHandoffData.processingSteps}
              statusText={agentHandoffData.statusText}
              lastHandoff={agentHandoffData.lastHandoff}
            />
          </div>
        )}

        {/* Degraded-generation notice (failed checks, unavailable providers) */}
        {!isUser && degradations.length > 0 && (
          <div className="ml-11">
            <DegradationNotice degradations={degradations} />
          </div>
        )}

        {/* Clarification acknowledgment */}
        {!isUser && clarificationAck && (
          <div className="ml-11">
            <ClarificationAckNotice ack={clarificationAck} />
          </div>
        )}

        {/* Memory/context pills */}
        {memories.length > 0 && (
          <div className="ml-11 flex flex-wrap gap-2 mb-3">
            {memories.map((memory: any, idx: number) => (
              <MemoryPill key={`${memory?.id || idx}`} memory={memory} index={idx} />
            ))}
          </div>
        )}

        {/* Saved artifact reference */}
        {artifactReference ? (
          <div className="ml-11">
            <ArtifactReferenceCard
              artifactId={artifactReference?.artifactId}
              artifactTitle={artifactReference?.artifactTitle}
              onArtifactClick={onArtifactClick}
            />
          </div>
        ) : (
        /* Message content */
        <div className="ml-11 prose prose-sm dark:prose-invert max-w-none">
          <ReactMarkdown
            components={{
              h1: ({ children }) => (
                <h1 className="text-2xl font-bold mb-3">{children}</h1>
              ),
              h2: ({ children }) => (
                <h2 className="text-xl font-semibold mb-2">{children}</h2>
              ),
              h3: ({ children }) => (
                <h3 className="text-lg font-medium mb-2">{children}</h3>
              ),
              p: ({ children }) => (
                <p className="mb-3 last:mb-0">{children}</p>
              ),
              strong: ({ children }) => (
                <strong className="font-semibold">{children}</strong>
              ),
              ul: ({ children }) => (
                <ul className="list-disc list-inside mb-3 space-y-1">
                  {children}
                </ul>
              ),
              ol: ({ children }) => (
                <ol className="list-decimal list-inside mb-3 space-y-1">
                  {children}
                </ol>
              ),
              li: ({ children }) => <li>{children}</li>,
              blockquote: ({ children }) => (
                <blockquote className="border-l-4 border-primary/60 pl-4 py-2 mb-3 bg-muted/30 rounded-r">
                  {children}
                </blockquote>
              ),
              code: ({ children }) => (
                <code className="bg-muted px-1 py-1 rounded text-sm font-mono">
                  {children}
                </code>
              ),
              pre: ({ children }) => (
                <pre className="bg-muted p-3 rounded-lg overflow-x-auto max-h-80 overflow-y-auto mb-3 text-sm font-mono">
                  {children}
                </pre>
              ),
              a: ({ children, href }) => (
                <a
                  href={href}
                  className="text-primary hover:underline"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                >
                  {children}
                </a>
              ),
            }}
          >
            {message?.content || message?.text || ''}
          </ReactMarkdown>
        </div>
        )}

        {/* Runtime tool result */}
        {runtimeTool && (
          <div className="ml-11">
            <RuntimeToolResultPanel
              state={runtimeTool.state || null}
              error={runtimeTool.error || null}
              connectionStatus={runtimeTool.state?.status || 'completed'}
              selectedToolLabel={runtimeTool.state?.label || null}
              onOpenArtifact={onArtifactClick}
            />
          </div>
        )}

        {/* Edited indicator */}
        {isEdited && (
          <div className="ml-11 text-xs text-muted-foreground mt-2">
            Edited{editedAt ? ` • ${formatEditedAt(editedAt)}` : ''}
          </div>
        )}

        {/* Agent metadata badge */}
        {!isUser && agentMetadata && (
          <div className="ml-11 text-xs text-white/50 mt-2">
            Generated by {agentMetadata.agentType} agent
          </div>
        )}

        {/* Reviewer callout */}
        {!isUser && reviewerFeedback && onRequestChanges && onViewDetails && (
          <div className="ml-11">
            <ReviewerCallout
              feedback={reviewerFeedback}
              onRequestChanges={() => onRequestChanges(reviewerFeedback)}
              onViewDetails={() => onViewDetails(reviewerFeedback)}
            />
          </div>
        )}

        {/* Feedback controls */}
        {!isUser && messageId && onFeedback && (
          <div className="ml-11 mt-3 article-feedback">
            <MessageFeedbackControls
              messageId={messageId}
              onFeedback={onFeedback}
              noteSentHint="Applied to future drafts when confidence passes."
            />
          </div>
        )}
      </div>
    </article>
  );
});

export default ArticleMessage;
